import { apiService, ApiResponse } from './api';

export interface MemberRegistrationData {
  // Personal Information
  fullName: string;
  dateOfBirth?: string;
  gender?: string;
  nicPassport?: string;
  profilePhoto?: string;
  
  // Contact Information
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  
  // Emergency Contact
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactNumber?: string;
  emergencyContactEmail?: string;
  
  // Health Information
  hasInjuriesOrConditions?: string;
  injuriesConditionsDetails?: string;
  underMedicalTreatment?: string;
  medicalTreatmentDetails?: string;
  advisedNotToExercise?: string;
  activityLevel?: string;
  
  // Membership Details
  membershipType?: string;
  startDate?: string;
  fitnessGoals?: string[];
  otherFitnessGoal?: string;
  peakHours?: string;
}

export interface MemberResponse {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  membershipType: string;
  status: string;
  startDate: string;
  profilePhoto: string | null;
  memberNumber: string;
  fitnessGoals: string[];
  createdAt: string;
  dateOfBirth?: string;
  gender?: string;
  nicPassport?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactNumber?: string;
  emergencyContactEmail?: string;
  hasInjuriesOrConditions?: string;
  injuriesConditionsDetails?: string;
  underMedicalTreatment?: string;
  medicalTreatmentDetails?: string;
  advisedNotToExercise?: string;
  activityLevel?: string;
  otherFitnessGoal?: string;
  peakHours?: string;
}

export interface PaginatedMemberResponse {
  hasPrevious: boolean;
  currentPage: number;
  hasNext: boolean;
  members: MemberResponse[];
  totalPages: number;
  totalElements: number;
  pageSize: number;
}

export interface MemberRegistrationResponse {
  memberId: number;
  memberNumber: string;
}

export interface MemberStatistics {
  totalMembers: number;
  activeMembers: number;
  suspendedMembers: number;
  expiredMembers: number;
  newMembers: number;
}

export interface MembersListParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  membershipType?: string;
}

class MemberService {
  
  /**
   * Register a new member
   */
  async registerMember(memberData: MemberRegistrationData): Promise<ApiResponse<MemberRegistrationResponse>> {
    return apiService.post<MemberRegistrationResponse>('/api/members', memberData);
  }

  /**
   * Update an existing member
   */
  async updateMember(id: number, memberData: MemberRegistrationData): Promise<ApiResponse<MemberResponse>> {
    return apiService.put<MemberResponse>(`/api/members/${id}`, memberData);
  }

  /**
   * Get paginated members list with optional filters
   */
  async getMembers(params: MembersListParams = {}): Promise<ApiResponse<PaginatedMemberResponse>> {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.membershipType) queryParams.append('membershipType', params.membershipType);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/members?${queryString}` : '/api/members';
    
    return apiService.get<PaginatedMemberResponse>(endpoint);
  }

  /**
   * Get member by ID
   */
  async getMemberById(id: number): Promise<ApiResponse<MemberResponse>> {
    return apiService.get<MemberResponse>(`/api/members/${id}`);
  }

  /**
   * Update member status
   */
  async updateMemberStatus(id: number, status: string): Promise<ApiResponse<void>> {
    return apiService.put<void>(`/api/members/${id}/status`, { status });
  }

  /**
   * Get member statistics
   */
  async getMemberStatistics(): Promise<ApiResponse<MemberStatistics>> {
    return apiService.get<MemberStatistics>('/api/members/statistics');
  }
}

export const memberService = new MemberService(); 